import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './VehicleSVGCustomizer.css';
import { ReactComponent as MyIcon } from '../../car.svg';
import { useVehicleOptions } from './useVehicleOptions';
import { searchVehicles, formatSearchResults } from './vehicleSearchService';
import VideoPlayer from '../VIDEO/VideoPlayer';
import EventNavigation from './EventNavigation';
import { useSharedEvents } from '../TIMEBAR/SharedEventsContext';
import { getVideoUrl, getVideoSegment, parseTimeToSeconds, formatTimeFromSeconds } from './videoResourceService';
import EmbeddedVideoPlayer from './EmbeddedVideoPlayer';
import { openExternalVideoPlayer } from "../Tabs/externalVideoPlayer";
import EnhancedSelect from './EnhancedSelect'; 
import { useAppStore } from '../../store/appStore';
import EnhancedVideoPlayer from './EnhancedVideoPlayer';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// 辅助函数：首字母大写
const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// 过滤器匹配函数
const getMatchingInfo = (descriptors, selectedFilters) => {
  // 如果没有过滤器提供或没有描述符，返回空
  if (!selectedFilters || !descriptors || descriptors.length === 0) {
    return { matched: [], mismatched: [] };
  }
  
  // 创建活跃过滤器数组（排除"none"值）
  const activeFilters = [];
  
  if (selectedFilters.type !== 'none') {
    activeFilters.push(`Type: ${capitalizeFirstLetter(selectedFilters.type)}`);
  }
  
  if (selectedFilters.color !== 'none') {
    activeFilters.push(`Color: ${capitalizeFirstLetter(selectedFilters.color)}`);
  }
  
  if (selectedFilters.model !== 'none') {
    activeFilters.push(`Model: ${capitalizeFirstLetter(selectedFilters.model)}`);
  }
  
  if (selectedFilters.stickers !== 'none') {
    activeFilters.push(`Stickers: ${capitalizeFirstLetter(selectedFilters.stickers)}`);
  }
  
  if (selectedFilters.bumper !== 'none') {
    activeFilters.push(`Bumper: ${capitalizeFirstLetter(selectedFilters.bumper)}`);
  }
  
  // 如果没有活跃过滤器，返回空
  if (activeFilters.length === 0) {
    return { matched: [], mismatched: [] };
  }
  
  // 计算匹配和不匹配特征
  const matched = [];
  const mismatched = [];
  
  // 检查每个活跃过滤器是否在描述符中
  activeFilters.forEach(filter => {
    const filterCategory = filter.split(':')[0].trim(); // 提取类别（Type、Color等）
    const filterDetail = filter.substring(filter.indexOf(':') + 1).trim(); // 提取详情
    
    // 查找对应类别的描述符
    const matchingDescriptor = descriptors.find(desc => 
      desc.startsWith(filterCategory + ':')
    );
    
    if (matchingDescriptor) {
      // 如果找到匹配类别的描述符，检查详情是否匹配
      const descriptorDetail = matchingDescriptor.substring(matchingDescriptor.indexOf(':') + 1).trim();
      
      // 如果过滤器为空，或者详情包含过滤器中的所有单词
      const filterWords = filterDetail.toLowerCase().split(' ').filter(word => word.length > 0);
      const descriptorLower = descriptorDetail.toLowerCase();
      
      // 如果所有单词都匹配，则视为匹配
      const allWordsMatch = filterWords.every(word => descriptorLower.includes(word));
      
      if (allWordsMatch) {
        matched.push(filter);
      } else {
        mismatched.push(filter);
      }
    } else {
      // 如果没有找到匹配类别的描述符，则视为不匹配
      mismatched.push(filter);
    }
  });
  
  return { matched, mismatched };
};
const VehicleTab = ({ trackedObjects = [] }) => {
  const [trackedVehicle, setTrackedVehicle] = useState(null);
  
  // ===== 状态管理 =====
  // 日历状态控制
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date(2025, 2, 22)); // 3月22日，月份从0开始
  
  // 使用优化的Hook获取选项数据
  const { options: vehicleOptions, isLoading: optionsLoading, error: optionsError, refreshOptions } = useVehicleOptions();
  
  // 搜索结果状态
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  
  // 新增：选项频率状态
  const [optionsFrequency, setOptionsFrequency] = useState({});
  
  // 时间块选择
  const [selectedTimeBlocks, setSelectedTimeBlocks] = useState(['11:00-11:30', '21:30-22:00']);
  
  // camera选择
  const [selectedCameras, setSelectedCameras] = useState([]);

  // 其他过滤器状态
  const [selectedConfidenceLevels, setSelectedConfidenceLevels] = useState([]);
  
  // 车辆部位选择状态
  const [selectedVehiclePart, setSelectedVehiclePart] = useState(null);
  
  // 描述符下拉选择状态
  const [descriptorValues, setDescriptorValues] = useState({
    type: 'none',
    color: 'none',
    model: 'none',
    stickers: 'none',
    bumper: 'none'
  });
  // 处理车辆部位选择
  const handleVehiclePartSelect = useCallback((partId) => {
    if (selectedVehiclePart === partId) {
      setSelectedVehiclePart(null);
    } else {
      setSelectedVehiclePart(partId);
      console.log(`Selected vehicle part: ${partId}`);
    }
  }, [selectedVehiclePart]);

  // 处理下拉框变化
  const handleSelectChange = useCallback((descriptor, value) => {
    setDescriptorValues(prev => ({
      ...prev,
      [descriptor]: value
    }));
    
    if (value !== 'none') {
      setSelectedVehiclePart(descriptor);
    } else if (selectedVehiclePart === descriptor) {
      setSelectedVehiclePart(null);
    }
  }, [selectedVehiclePart]);
  
  // 定义车辆部位按钮
  const vehicleParts = useMemo(() => [
    { id: 'type', label: 'Type', top: '10%', left: '50%' },
    { id: 'color', label: 'Color', top: '48%', left: '68%' },
    { id: 'model', label: 'Model', top: '58%', left: '53%'},
    { id: 'stickers', label: 'Stickers', top: '28%', left: '75%' },
    { id: 'bumper', label: 'Bumper', top: '68%', left: '85%' }
  ], []);

  // 已选择的描述符数量
  const selectedDescriptors = useMemo(() => Object.entries(descriptorValues)
    .filter(([_, value]) => value !== 'none')
    .map(([key, _]) => key), [descriptorValues]);
  
  // 修改：处理时间块选择
  const handleTimeBlockSelect = useCallback((timeBlock) => {
    const selectableBlocks = ['11:00-11:30', '21:30-22:00'];
    if (!selectableBlocks.includes(timeBlock)) return;
    
    setSelectedTimeBlocks(prev => {
      if (prev.includes(timeBlock)) {
        return prev.filter(block => block !== timeBlock);
      } else {
        return [...prev, timeBlock];
      }
    });
  }, []);

  // 处理相机选择
  const handleCameraSelect = useCallback((cameraNum) => {
    setSelectedCameras(prev => 
      prev.includes(cameraNum) 
        ? prev.filter(num => num !== cameraNum) 
        : [...prev, cameraNum]
    );
  }, []);
  
  // 处理置信度选择
  const handleConfidenceLevelSelect = useCallback((level) => {
    setSelectedConfidenceLevels(prev =>
      prev.includes(level) 
        ? prev.filter(l => l !== level) 
        : [...prev, level]
    );
  }, []);

  // 获取颜色值
  const getColorValue = useCallback((colorName) => {
    const colorMap = {
      'none': 'rgba(255, 255, 255, 0.29)',
      'black': '#000000',
      'white': '#ffffff',
      'blue': '#0d6efd',
      'red': '#dc3545',
      'silver': '#c0c0c0',
      'gray': '#6c757d',
      'orange': '#ffa500',
      'purple': '#9c27b0',
      'pink': '#ffd1dc',
      'green': '#198754',
      'yellow': '#ffc107',
      'brown': '#8b4513',
      'beige': '#f5f5dc',
      'tan': '#d2b48c',
      'olive': '#808000',
      'gold': '#ffd700',
      'navy': '#000080',
      'maroon': '#800000'
    };
    return colorMap[colorName] || 'rgba(255, 255, 255, 0.6)';
  }, []);

  // 新增：更新选项频率统计的函数
  const updateOptionsFrequency = useCallback((results, currentFilters = null) => {
    if (!results) return;

    const frequency = {};
    const allDescriptors = ['type', 'color', 'model', 'stickers', 'bumper'];
    
    // 初始化所有特征的频率
    allDescriptors.forEach(key => {
      frequency[key] = {};
    });

    // 对每个特征，应用除了当前特征外的所有过滤器
    allDescriptors.forEach(descriptor => {
      // 获取应该参与过滤的结果
      let filteredResults = results;
      
      // 如果提供了当前过滤器，应用除了当前descriptor外的所有过滤器
      if (currentFilters) {
        const filtersWithoutCurrent = { ...currentFilters };
        filtersWithoutCurrent[descriptor] = 'none'; // 移除当前特征的过滤器
        
        // 应用其他过滤器（简化版）
        filteredResults = results.filter(result => {
          for (const [key, value] of Object.entries(filtersWithoutCurrent)) {
            if (value === 'none') continue;
            
            // 检查结果中是否有匹配的描述符
            const foundDescriptor = result.descriptors.find(desc => 
              desc.toLowerCase().startsWith(key.toLowerCase() + ':')
            );
            
            if (!foundDescriptor) return false;
            
            // 提取值并检查是否包含过滤器值
            const descriptorValue = foundDescriptor
              .substring(foundDescriptor.indexOf(':') + 1)
              .trim()
              .toLowerCase();
              
            if (!descriptorValue.includes(value.toLowerCase())) return false;
          }
          return true;
        });
      }

      // 统计过滤后结果中当前特征的频率
      filteredResults.forEach(result => {
        // 找到当前描述符
        const currentDesc = result.descriptors.find(desc => 
          desc.toLowerCase().startsWith(descriptor.toLowerCase() + ':')
        );
        
        if (currentDesc) {
          // 提取值
          const value = currentDesc
            .substring(currentDesc.indexOf(':') + 1)
            .trim()
            .toLowerCase();
          
          // 将值分词，支持多个值（如"Blue Sedan"）
          const values = value.split(' ');
          
          // 对每个值进行计数
          values.forEach(val => {
            if (val && val !== 'none') {
              frequency[descriptor][val] = (frequency[descriptor][val] || 0) + 1;
            }
          });
        }
      });
    });
    
    setOptionsFrequency(frequency);
  }, []);

  // 新增：处理柱状图过滤器点击
  const handleBarSelect = useCallback((descriptor, value) => {
    setDescriptorValues(prev => {
      const newValues = {
        ...prev,
        [descriptor]: prev[descriptor] === value ? 'none' : value
      };
      
      // 更新特征频率，传入新的过滤器值
      updateOptionsFrequency(searchResults, newValues);
      
      return newValues;
    });
  }, [searchResults, updateOptionsFrequency]);

  // 新增：重置单个过滤器
  const handleResetFilter = useCallback((descriptor) => {
    setDescriptorValues(prev => {
      const newValues = {
        ...prev,
        [descriptor]: 'none'
      };
      
      // 更新特征频率，传入新的过滤器值
      updateOptionsFrequency(searchResults, newValues);
      
      return newValues;
    });
  }, [searchResults, updateOptionsFrequency]);

  // 新增：柱状图过滤器渲染函数
  const renderBarChartFilter = useCallback((descriptor, label) => {
    const frequency = optionsFrequency[descriptor] || {};
    const options = Object.keys(frequency).filter(key => key && key !== 'none').sort();
    
    const maxFrequency = Math.max(...Object.values(frequency), 1);
    
    return (
      <div className="vehicle-filter-item" style={{
        backgroundColor: '#2a2a2a',
        borderRadius: '4px',
        padding: '10px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
      }}>
        <div className="vehicle-filter-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <h4 className="vehicle-bar-filter-title" style={{
            margin: '0',
            fontSize: '14px',
            fontWeight: '500',
            color: '#ffffff'
          }}>{label}</h4>
          {descriptorValues[descriptor] !== 'none' && (
            <button 
              className="vehicle-reset-filter"
              onClick={() => handleResetFilter(descriptor)}
              title="Reset this filter"
              style={{
                background: 'none',
                border: 'none',
                color: '#999',
                cursor: 'pointer',
                padding: '0',
                fontSize: '12px'
              }}
            >
              clear
            </button>
          )}
        </div>
        <div className="vehicle-bar-chart" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          width: '100%'
        }}>
          {options.map(option => {
            const count = frequency[option] || 0;
            const percentage = Math.max(10, Math.round((count / maxFrequency) * 100));
            const isSelected = descriptorValues[descriptor] === option;
            
            return (
              <div 
                key={option}
                className={`vehicle-bar-item ${isSelected ? 'vehicle-bar-item--selected' : ''}`}
                onClick={() => handleBarSelect(descriptor, option)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '2px 0',
                  cursor: 'pointer',
                  borderRadius: '2px',
                  transition: 'all 0.2s ease',
                  border: isSelected ? '1px solid #2196F3' : '1px solid transparent',
                  backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                  marginBottom: '1px'
                }}
              >
                <div className="vehicle-bar-label" style={{
                  fontSize: '12px',
                  width: '35%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: '#e9ecef',
                  minWidth: '0',
                  paddingRight: '4px'
                }}>
                  {capitalizeFirstLetter(option)}
                </div>
                <div className="vehicle-bar-container" style={{
                  position: 'relative',
                  height: '12px',
                  backgroundColor: '#333',
                  borderRadius: '2px',
                  flex: '1',
                  minWidth: '0',
                  marginRight: '4px'
                }}>
                  <div 
                    className="vehicle-bar-fill"
                    style={{
                      width: `${percentage}%`,
                      height: '12px',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease',
                      position: 'relative',
                      backgroundColor: descriptor === 'color' ? getColorValue(option) : '#2196F3'
                    }}
                  ></div>
                  <span className="vehicle-bar-count" style={{
                    position: 'absolute',
                    right: '2px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '9px',
                    color: '#e9ecef',
                    whiteSpace: 'nowrap'
                  }}>
                    {count}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [optionsFrequency, descriptorValues, handleResetFilter, handleBarSelect, getColorValue]);
// 清除所有筛选条件
const handleClearFilters = useCallback(() => {
  // 重置所有筛选值
  setDescriptorValues({
    type: 'none',
    color: 'none',
    model: 'none',
    stickers: 'none',
    bumper: 'none'
  });
  
  setSelectedVehiclePart(null);
  setSelectedTimeBlocks(['11:00-11:30', '21:30-22:00']);
  setSelectedCameras([]);
  setSelectedConfidenceLevels([]);
  
  // 直接发送一个不带参数的请求获取所有结果
  setIsSearching(true);
  setSearchError(null);
  
  // 使用延时确保UI先更新
  setTimeout(() => {
    // 直接调用无参数的API获取所有结果
    fetch('${API_URL}/api/search/vehicle')
      .then(response => {
        if (!response.ok) throw new Error(`服务器错误: ${response.statusText}`);
        return response.json();
      })
      .then(data => {
        console.log('获取所有车辆结果:', data);
        const formattedResults = formatVehicleResults(data);
        setSearchResults(formattedResults);
        // 更新频率统计
        updateOptionsFrequency(formattedResults);
      })
      .catch(error => {
        console.error("搜索错误:", error);
        setSearchError(`搜索失败: ${error.message}`);
        setSearchResults([]);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }, 300);
}, [updateOptionsFrequency]);

// 辅助函数：格式化车辆结果
const formatVehicleResults = useCallback((data) => {
  return data.map(vehicle => {
    // 处理第一个外观（如果存在）
    const appearance = vehicle.appearances && vehicle.appearances.length > 0 ? vehicle.appearances[0] : {};
    
    // 提取图像路径
    const imageUrl = appearance.imagePath ? 
      `${API_BASE_URL}/img/${appearance.imagePath.replace('/static/', '')}` : null;
    
    // 提取视频信息
    const videoInfo = appearance.videoId ? 
      `${appearance.videoId} (${appearance.timestamp || 'N/A'}, ${appearance.time || 'N/A'})` : 'No video info';
    
    // 提取视频路径
    let videoPath = appearance.videoSegment?.videoPath || null;
    
    // 如果没有明确的路径，基于 videoId 构建一个
    if (!videoPath && appearance.videoId) {
      // 检查 videoId 的格式，构建合适的路径
      if (appearance.videoId.startsWith('N')) {
        // 夜间视频
        const videoNumber = appearance.videoId.match(/\d+/)[0];
        videoPath = `/night/videos/video${videoNumber}.mp4`;
      } else if (appearance.videoId.startsWith('D')) {
        // 白天视频
        const videoNumber = appearance.videoId.match(/\d+/)[0];
        videoPath = `/day/videos/video${videoNumber}.mp4`;
      } else {
        // 通用格式
        videoPath = `/videos/${appearance.videoId}.mp4`;
      }
    }
    
    // 提取时间戳
    const startTime = appearance.videoSegment?.startTime || appearance.timestamp || null;
    const endTime = appearance.videoSegment?.endTime || null;
    
    return {
      id: vehicle.id,
      imageUrl: imageUrl,
      descriptors: formatVehicleFeatures(vehicle.features),
      videoInfo: videoInfo,
      videoPath: videoPath,
      videoStartTime: startTime,
      videoEndTime: endTime,
      rawData: vehicle
    };
  });
}, []);
// 处理搜索
const handleSearch = useCallback(async () => {
  try {
    setIsSearching(true);
    setSearchError(null);
    
    // 构建搜索参数 - 只添加非'none'的值
    const params = new URLSearchParams();
    
    if (descriptorValues.type !== 'none') params.append('type', descriptorValues.type);
    if (descriptorValues.color !== 'none') params.append('color', descriptorValues.color);
    if (descriptorValues.model !== 'none') params.append('model', descriptorValues.model);
    if (descriptorValues.stickers !== 'none') params.append('stickers', descriptorValues.stickers);
    if (descriptorValues.bumper !== 'none') params.append('bumper', descriptorValues.bumper);
    
    // 相机和时间块
    if (selectedCameras.length > 0) {
      params.append('cameras', selectedCameras.join(','));
    }
    
    if (selectedTimeBlocks.length > 0) {
      params.append('timeBlocks', selectedTimeBlocks.join(','));
    }
    
    console.log('搜索参数:', Object.fromEntries(params));
    
    const response = await fetch(`${API_BASE_URL}/api/search/vehicle?${params.toString()}`);
    if (!response.ok) throw new Error(`服务器错误: ${response.statusText}`);
    
    const data = await response.json();
    
    // 格式化结果
    const formattedResults = formatVehicleResults(data);
    
    // 计算所有活跃的过滤条件
    const activeFilters = [];

    // 提取活跃过滤条件
    if (descriptorValues.type !== 'none') {
      activeFilters.push(`Type: ${capitalizeFirstLetter(descriptorValues.type)}`);
    }
    if (descriptorValues.color !== 'none') {
      activeFilters.push(`Color: ${capitalizeFirstLetter(descriptorValues.color)}`);
    }
    if (descriptorValues.model !== 'none') {
      activeFilters.push(`Model: ${capitalizeFirstLetter(descriptorValues.model)}`);
    }
    if (descriptorValues.stickers !== 'none') {
      activeFilters.push(`Stickers: ${capitalizeFirstLetter(descriptorValues.stickers)}`);
    }
    if (descriptorValues.bumper !== 'none') {
      activeFilters.push(`Bumper: ${capitalizeFirstLetter(descriptorValues.bumper)}`);
    }

    // 计算匹配度并排序结果
    const sortedResults = formattedResults.map(result => {
      // 计算匹配信息
      let matchCount = 0;
      const matchingFilters = [];
      
      activeFilters.forEach(filter => {
        const filterCategory = filter.split(':')[0].trim();
        const filterDetail = filter.substring(filter.indexOf(':') + 1).trim().toLowerCase();
        
        // 查找匹配描述符
        const matchingDescriptor = result.descriptors.find(desc => 
          desc.toLowerCase().startsWith(filterCategory.toLowerCase() + ':')
        );
        
        // 计算匹配逻辑
        if (matchingDescriptor) {
          const descriptorDetail = matchingDescriptor
            .substring(matchingDescriptor.indexOf(':') + 1)
            .trim()
            .toLowerCase();
          
          // 检查是否包含所有单词
          const filterWords = filterDetail.split(' ').filter(word => word.length > 0);
          const allWordsMatch = filterWords.every(word => 
            descriptorDetail.includes(word.toLowerCase())
          );
          
          if (allWordsMatch) {
            matchCount++;
            matchingFilters.push(filter);
          }
        }
      });
      
      // 返回带有匹配信息的结果
      return { 
        ...result, 
        matchCount,
        matchingFilters,
        matchPercentage: activeFilters.length > 0 
          ? Math.round((matchCount / activeFilters.length) * 100) 
          : 0
      };
    }).sort((a, b) => {
      // 只有当有活跃过滤器时才排序
      if (activeFilters.length > 0) {
        // 按匹配数量排序（降序）
        if (b.matchCount !== a.matchCount) {
          return b.matchCount - a.matchCount;
        }
        // 如果匹配数量相同，按匹配百分比排序
        return b.matchPercentage - a.matchPercentage;
      }
      return 0; // 保持原始顺序
    });

    setSearchResults(sortedResults);
    // 更新频率统计
    updateOptionsFrequency(sortedResults, descriptorValues);
  } catch (error) {
    console.error('搜索失败:', error);
    setSearchError(`搜索失败: ${error.message}`);
  } finally {
    setIsSearching(false);
  }
}, [descriptorValues, selectedCameras, selectedTimeBlocks, formatVehicleResults, updateOptionsFrequency]);

// 辅助函数：格式化车辆特征
function formatVehicleFeatures(features) {
  if (!features) return [];
  const result = [];
  if (features.type) result.push(`Type: ${capitalizeFirstLetter(features.type)}`);
  if (features.color) result.push(`Color: ${capitalizeFirstLetter(features.color)}`);
  if (features.model) result.push(`Model: ${capitalizeFirstLetter(features.model)}`);
  if (features.stickers) result.push(`Stickers: ${capitalizeFirstLetter(features.stickers)}`);
  if (features.bumper) result.push(`Bumper: ${capitalizeFirstLetter(features.bumper)}`);
  return result;
}

// 获取车辆部位按钮样式
const getVehiclePartButtonStyle = useCallback((partId) => {
  // 只对颜色按钮应用自定义颜色
  if (partId === 'color' && descriptorValues.color !== 'none') {
    // 获取颜色值
    const selectedColor = getColorValue(descriptorValues.color.toLowerCase());
    
    // 判断背景色亮暗，决定文本颜色
    const isLightColor = 
      selectedColor === '#ffffff' || 
      selectedColor === '#ffff00' || 
      selectedColor === '#ffd700' || 
      selectedColor === '#f5f5dc' || 
      selectedColor === '#d2b48c' || 
      selectedColor === '#ffc0cb' || 
      selectedColor === '#ff7f50' || 
      selectedColor === '#c0c0c0';
    
    return {
      backgroundColor: selectedColor,
      color: isLightColor ? '#000000' : '#ffffff',
      // 如果当前选中，继续显示蓝色边框
      boxShadow: selectedVehiclePart === partId ? 
        `0 0 0 2px #2196F3, 0 0 8px rgba(33, 150, 243, 0.8)` : 
        '0 0 4px rgba(0, 0, 0, 0.4)',
      // 确保按钮正确尺寸以适应边框效果
      border: selectedVehiclePart === partId ? '2px solid transparent' : 'none'
    };
  }
  
  // 对其他按钮或未选择颜色时，返回null以使用默认样式
  return null;
}, [descriptorValues.color, selectedVehiclePart, getColorValue]);

// 处理保存结果
const handleSaveResult = useCallback((result) => {
  console.log('保存车辆结果:', result);
  // 这里可以添加更多保存逻辑
}, []);

// 处理视频播放
const handlePlayVideo = useCallback((videoPath, startTime, endTime) => {
  if (!videoPath) {
    console.error('No video path provided');
    return;
  }
  
  // 使用openExternalVideoPlayer函数
  openExternalVideoPlayer(videoPath, startTime, endTime);
  
  console.log(`Opening video: ${videoPath} from ${startTime} to ${endTime}`);
}, []);

// 处理详情查看
const handleGoDetail = useCallback((videoPath, startTime) => {
  if (!videoPath) {
    console.error('No video path provided');
    return;
  }

  // 从视频路径提取机器人ID
  const videoNumberMatch = videoPath.match(/video(\d+)\.mp4/);
  if (!videoNumberMatch) {
    console.error('Could not extract video number from path:', videoPath);
    return;
  }
  
  const robotId = parseInt(videoNumberMatch[1], 10);
  const startTimeSeconds = parseTimeToSeconds(startTime) || 0;
  
  // 直接使用appStore的更新方法
  const { updateVideoState } = useAppStore.getState();
  
  // 更新全局状态以切换视频并导航到特定时间
  updateVideoState({
    videoUrl: videoPath,
    robotId: robotId,
    time: startTimeSeconds,
    isPlaying: true,
    currentTime: startTimeSeconds,
    eventId: null,
    lastVideoSwitch: Date.now()
  }, 'timeline_event_click');
  
  console.log(`Go Details: Switch to robot ${robotId}, navigate to ${startTime} (${startTimeSeconds}s)`);
}, []);

// 监听描述符值变化，自动执行搜索
useEffect(() => {
  // 检查是否有任何非'none'的筛选值
  const hasAnyValue = Object.values(descriptorValues).some(value => value !== 'none');
  
  // 如果有值，触发搜索
  if (hasAnyValue) {
    // 使用延迟防止过于频繁的搜索
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);
    
    // 清理函数防止内存泄漏
    return () => clearTimeout(timer);
  }
}, [descriptorValues, handleSearch]);

// 扩展 useEffect 监听所有筛选条件
useEffect(() => {
  // 检查是否有任何筛选条件
  const hasDescriptors = Object.values(descriptorValues).some(value => value !== 'none');
  const hasCameras = selectedCameras.length > 0;
  const hasConfidence = selectedConfidenceLevels.length > 0;
  // 时间块默认是选择的，只有当它不是默认值时才触发
  const hasCustomTimeBlocks = 
    selectedTimeBlocks.length !== 2 || 
    !selectedTimeBlocks.includes('11:00-11:30') || 
    !selectedTimeBlocks.includes('21:30-22:00');
  
  // 如果有任何筛选条件，触发搜索
  if (hasDescriptors || hasCameras || hasConfidence || hasCustomTimeBlocks) {
    // 添加搜索状态指示
    setIsSearching(true);
    
    // 使用延迟防止过于频繁的搜索
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);
    
    return () => clearTimeout(timer);
  }
}, [descriptorValues, selectedCameras, selectedTimeBlocks, selectedConfidenceLevels, handleSearch]);

// 在组件挂载时获取所有车辆数据
useEffect(() => {
  const fetchAllVehicles = async () => {
    try {
      const response = await fetch('${API_BASE_URL}/api/search/vehicle');
      if (response.ok) {
        const data = await response.json();
        console.log('获取到的车辆数据:', data);
        
        // 格式化结果
        const formattedResults = formatVehicleResults(data);
        setSearchResults(formattedResults);
        
        // 更新频率统计
        updateOptionsFrequency(formattedResults);
      }
    } catch (error) {
      console.error('获取车辆数据失败:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  fetchAllVehicles();
}, [formatVehicleResults, updateOptionsFrequency]);

// 处理从跟踪对象中更新特征
useEffect(() => {
  if (trackedObjects && trackedObjects.length > 0) {
    const lastTracked = trackedObjects[trackedObjects.length - 1];
    if (lastTracked.features) {
      setDescriptorValues(prev => ({
        ...prev,
        ...lastTracked.features
      }));
    }
  }
}, [trackedObjects]);

// 创建渲染跟踪车辆的函数
const renderTrackedVehicles = useCallback(() => {
  if (!trackedObjects || trackedObjects.length === 0) {
    return (
      <div className="vehicle-details-placeholder">
        No vehicles currently being tracked
      </div>
    );
  }
  
  return (
    <div className="vehicle-svg-details">
      <h3>Tracked Vehicles ({trackedObjects.length})</h3>
      <div className="vehicle-objects-list">
        {trackedObjects.map((vehicle, index) => (
          <div key={index} className="vehicle-object-item">
            <div className="vehicle-object-image">
              {vehicle.imagePath ? (
                <img 
                  src={vehicle.imagePath} 
                  alt={vehicle.label || 'Vehicle'}
                  className="tracked-vehicle-img"
                  onError={(e) => { e.target.src = "/api/placeholder/100/100?text=Vehicle"; }}
                />
              ) : (
                <div className="vehicle-placeholder-image">No Image</div>
              )}
            </div>
            <div className="vehicle-object-details">
              <h4>{vehicle.label || `Vehicle ${index + 1}`}</h4>
              <p className="vehicle-tracked-time">Detected at: {vehicle.timestamp || 'N/A'}</p>
              <p className="vehicle-tracked-description">{vehicle.summary || 'No description available'}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}, [trackedObjects]);

// 加载状态标志
const isLoading = optionsLoading || isSearching;

return (
  <div className="vehicle-customizer" style={{
    backgroundColor: '#1a1a1a',
    color: 'white',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  }}>
    {/* 搜索区域 */}
    <div className="vehicle-search-section" style={{
      height: '42%', // 固定高度为30%
      padding: '15px',
      borderBottom: '1px solid #444',
      backgroundColor: '#1a1a1a',
      overflowY: 'auto' // 添加滚动条以防内容过多
    }}>
      <h2 className="vehicle-search-title" style={{
        margin: '0 0 10px 0',
        fontSize: '16px',
        fontWeight: '500'
      }}>
        Descriptor: {selectedDescriptors.length} chosen
      </h2>
      
      {/* 过滤器容器 - 横向排列 */}
      <div className="vehicle-filter-content" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '15px',
        width: '100%'
      }}>
        {renderBarChartFilter('type', 'Type')}
        {renderBarChartFilter('color', 'Color')}
        {renderBarChartFilter('model', 'Model')}
        {renderBarChartFilter('stickers', 'Stickers')}
        {renderBarChartFilter('bumper', 'Bumper')}
      </div>
      
      {/* 搜索按钮区域 - 与Person tab样式一致 */}
      <div className="vehicle-search-actions" style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        marginTop: '15px'
      }}>
        <button 
          className="vehicle-search-button" 
          onClick={handleSearch}
          disabled={isLoading}
          style={{
            padding: '6px 16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {isSearching ? '...' : 'Search'}
        </button>
        <button 
          className="vehicle-clear-button" 
          onClick={handleClearFilters}
          disabled={isLoading}
          style={{
            padding: '6px 16px',
            backgroundColor: '#4b5563',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Clear
        </button>
      </div>
    </div>
    
    {/* 结果区域 */}
    <div className="vehicle-results-section" style={{
      height: '58%', // 固定高度为70%
      overflowY: 'auto',
      padding: '15px',
      backgroundColor: '#1a1a1a'
    }}>
      {/* 显示错误信息 */}
      {searchError && (
        <div className="vehicle-error-message" style={{
          padding: '10px',
          margin: '10px 0',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{searchError}</span>
          <button 
            onClick={() => setSearchError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#721c24',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* 显示搜索结果 */}
      {searchResults.length > 0 ? (
        <div className="vehicle-results-container">
          {searchResults.map((vehicle, index) => (
            <VehicleResultCard 
              key={`${vehicle.id}-${index}`}
              imageUrl={vehicle.imageUrl}
              descriptors={vehicle.descriptors || []}
              id={vehicle.id}
              videoInfo={vehicle.videoInfo}
              videoPath={vehicle.videoPath}
              videoStartTime={vehicle.videoStartTime}
              videoEndTime={vehicle.videoEndTime}
              onSave={() => handleSaveResult(vehicle.rawData || vehicle)}
              rawData={vehicle.rawData}
              selectedFilters={descriptorValues}
              onPlayVideo={handlePlayVideo}
              matchCount={vehicle.matchCount || 0}
              matchPercentage={vehicle.matchPercentage || 0}
              onGoDetail={handleGoDetail}
            />
          ))}
        </div>
      ) : (
        <>
          {/* 如果执行了搜索但没有结果，显示无结果消息 */}
          {selectedDescriptors.length > 0 || selectedCameras.length > 0 || 
            selectedConfidenceLevels.length > 0 ? (
            <div className="vehicle-no-results" style={{
              padding: '20px',
              textAlign: 'center',
              color: '#aaa',
              fontSize: '16px',
              marginTop: '20px'
            }}>
              No matching results found. Try adjusting your filters.
            </div>
          ) : (
            /* 如果没有执行搜索，显示示例结果 */
            <div className="vehicle-results-container">
              {/* 示例卡片 */}
              <VehicleResultCard 
                imageUrl="/static/frames/night/video2/vehicle/vehicle_1_001.png"
                descriptors={["Type: Van", "Color: Silver", "Model: Tesla Model 3", "Bumper: Damaged"]}
                id="vehicle_1"
                videoInfo="video2 (00:00:20, night)"
                videoPath="/night/videos/video2.mp4"
                videoStartTime="00:00:15"
                videoEndTime="00:00:25"
                onSave={() => handleSaveResult({id: "vehicle_1"})}
                rawData={{
                  id: "vehicle_1",
                  category: "vehicle",
                  features: {
                    type: "van", 
                    stickers: "family", 
                    color: "silver", 
                    model: "Tesla Model 3", 
                    bumper: "damaged"
                  },
                  appearances: [{
                    imagePath: "/static/frames/night/video2/vehicle/vehicle_1_001.png",
                    videoId: "video2",
                    time: "night",
                    timestamp: "00:00:20",
                    videoSegment: {
                      videoPath: "/static/videos/night/video2.mp4",
                      startTime: "00:00:15",
                      endTime: "00:00:25"
                    }
                  }]
                }}
                selectedFilters={descriptorValues}
                onPlayVideo={handlePlayVideo}
                onGoDetail={handleGoDetail}
              />
            </div>
          )}
        </>
      )}
    </div>
  </div>
);
};

const VehicleResultCard = ({ 
  imageUrl, 
  descriptors, 
  id, 
  videoInfo, 
  videoPath,
  videoStartTime,
  videoEndTime,
  onSave,
  rawData,
  selectedFilters,
  onPlayVideo,
  matchCount, 
  matchPercentage,
  onGoDetail
}) => {
  const [imageError, setImageError] = useState(false);
  const { addToWorkspace, addSharedEvent } = useSharedEvents();
  const [showEmbeddedVideo, setShowEmbeddedVideo] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [saveNote, setSaveNote] = useState('');
  const [actionRequired, setActionRequired] = useState('monitor');

  // Get matching info
  const { matched, mismatched } = getMatchingInfo(descriptors, selectedFilters);
  
  // Get border color based on match status
  const getBorderColor = () => {
    if (matched.length === 0) return '#444'; // Default border color
    if (mismatched.length === 0) return '#4caf50'; // Green for full match
    return '#ff9800'; // Orange for partial match
  };

  // Get card class name
  const getCardClassName = () => {
    let className = "vehicle-result-card";
    if (matched.length > 0) {
      if (mismatched.length === 0) {
        className += " vehicle-card--match-all";
      } else {
        className += " vehicle-card--match-partial";
      }
    } else {
      className += " vehicle-card--no-match";
    }
    return className;
  };

  // Handle save to workspace
  const handleSaveToWorkspace = () => {
    if (onSave) {
      onSave();
    }
    
    // Parse videoStartTime if it's a string
    let parsedStartTime = videoStartTime;
    if (typeof videoStartTime === 'string' && videoStartTime.includes(':')) {
      // Convert "HH:MM:SS" format to seconds
      const parts = videoStartTime.split(':');
      if (parts.length === 3) {
        parsedStartTime = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
      } else if (parts.length === 2) {
        parsedStartTime = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    }
    
    // Ensure parsedStartTime is a number
    if (isNaN(parsedStartTime)) {
      parsedStartTime = 0;
    }
    
    const uniqueId = `vehicle-${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create workspace event object
    const workspaceEvent = {
      id: uniqueId,
      source: 'vehicle',
      type: 'vehicle',
      descriptors: descriptors,
      imageUrl: imageUrl,
      imagePath: imageUrl, // Keep original imageUrl as imagePath
      videoInfo: videoInfo,
      videoPath: videoPath,
      videoStartTime: parsedStartTime,
      videoEndTime: videoEndTime,
      event: `Vehicle ${id}`,
      robotId: robotNumber,
      timeframe: videoType === 'Day' ? '11AM' : videoType === 'Night' ? '11PM' : null,
      startTime: parsedStartTime,
      savedAt: new Date().toISOString(),
      savedAtDisplay: new Date().toLocaleString(),
      rawData: rawData,
      savedTo: ['workspace'],
      notes: saveNote ? [
        {
          id: Date.now().toString(),
          content: saveNote,
          timestamp: new Date().toISOString(),
          isImportant: false
        }
      ] : []
    };
    
    // Add to workspace
    addToWorkspace(workspaceEvent);
    setShowSaveModal(false);
    setSaveNote('');
  };

  // Handle share to shared space
  const handleShareToShared = () => {
    // Parse videoStartTime if it's a string
    let parsedStartTime = videoStartTime;
    if (typeof videoStartTime === 'string' && videoStartTime.includes(':')) {
      // Convert "HH:MM:SS" format to seconds
      const parts = videoStartTime.split(':');
      if (parts.length === 3) {
        parsedStartTime = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
      } else if (parts.length === 2) {
        parsedStartTime = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    }
    
    // Ensure parsedStartTime is a number
    if (isNaN(parsedStartTime)) {
      parsedStartTime = 0;
    }
    
    const uniqueId = `vehicle-shared-${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create the event object to share
    const sharedEvent = {
      id: uniqueId,
      source: 'vehicle',
      type: 'vehicle',
      descriptors: descriptors,
      imageUrl: imageUrl,
      imagePath: imageUrl, // Keep original imageUrl as imagePath
      videoInfo: videoInfo,
      videoPath: videoPath,
      videoStartTime: parsedStartTime,
      videoEndTime: videoEndTime,
      event: `Vehicle ${id}`,
      robotId: robotNumber,
      timeframe: videoType === 'Day' ? '11AM' : videoType === 'Night' ? '11PM' : null,
      startTime: parsedStartTime,
      savedAt: new Date().toISOString(),
      savedAtDisplay: new Date().toLocaleString(),
      rawData: rawData,
      shareMessage: shareMessage,
      actionRequired: actionRequired,
      priority: actionRequired === 'dispatch' ? 1 : 
               actionRequired === 'investigate' ? 2 : 
               actionRequired === 'monitor' ? 3 : 4,
      messages: [{
        id: Date.now().toString(),
        sender: "Current Officer",
        message: shareMessage,
        timestamp: new Date().toISOString(),
      }],
      savedTo: ['shared']
    };
    
    // Add to shared events
    addSharedEvent(sharedEvent);
    
    // Close the modal
    setShowShareModal(false);
    setShareMessage('');
    setActionRequired('monitor');
  };

  // Handle video play
  const handlePlayClick = () => {
    setShowEmbeddedVideo(prev => !prev);
  };

  // Extract video type
  const videoType = videoInfo && videoInfo.includes('(') ? 
    (videoInfo.includes('day') ? 'Day' : videoInfo.includes('night') ? 'Night' : '') : '';
  
  // Extract robot number
  const extractRobotNumber = () => {
    if (!videoInfo) return null;
    
    // Try to match "N8_1080p" or "D6_1080p" format
    const formatMatch = videoInfo.match(/[DN](\d+)_/);
    if (formatMatch && formatMatch[1]) {
      return parseInt(formatMatch[1], 10);
    }
    
    // Fall back to simpler "N8" or "D6" format
    const simpleMatch = videoInfo.match(/[DN](\d+)/);
    if (simpleMatch && simpleMatch[1]) {
      return parseInt(simpleMatch[1], 10);
    }
    
    // Try to match "video2" format
    const videoMatch = videoInfo.match(/video(\d+)/i);
    if (videoMatch && videoMatch[1]) {
      return parseInt(videoMatch[1], 10);
    }
    
    return null;
  };
  
  const robotNumber = extractRobotNumber();

  return (
    <div className={getCardClassName()} style={{
      border: `2px solid ${getBorderColor()}`,
      boxShadow: matched.length > 0 ? `0 2px 8px rgba(${mismatched.length === 0 ? '76, 175, 80' : '255, 152, 0'}, 0.3)` : 'none',
      position: 'relative'
    }}>
      {/* Robot number badge */}
      {robotNumber && (
        <div className="vehicle-robot-badge" style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: videoType === 'Day' ? '#2196F3' : '#673AB7',
          color: 'white',
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '14px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
        }}>
          {robotNumber}
        </div>
      )}
      
      {/* Left side image area */}
      <div className="vehicle-result-image">
        <div className="vehicle-object-image">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={`Vehicle ${id}`} 
              className="vehicle-result-img"
              onError={(e) => {
                console.error(`Image load failed: ${imageUrl}`);
                // Try backup method
                const fileName = imageUrl.split('/').pop();
                e.target.src = `${API_BASE_URL}/img/${fileName}`;
              }}
            />
          ) : (
            <div className="vehicle-video-placeholder">
              No image available
            </div>
          )}
        </div>
      </div>
      
      {/* Right side details area */}
      <div className="vehicle-result-details">
        {/* Add explicit video type info at the top */}
        <div className="vehicle-video-type" style={{
          background: videoType === 'Day' ? '#90caf9' : '#7986cb',
          color: '#fff',
          padding: '2px 6px',
          borderRadius: '3px',
          display: 'inline-block',
          fontSize: '12px',
          marginBottom: '8px',
          fontWeight: 'bold'
        }}>
          {videoType} Video {robotNumber ? `#${robotNumber}` : ''}
        </div>
        
        {/* Descriptor matching info */}
        <div className="vehicle-descriptor-info">
          <h3 className="vehicle-result-subtitle">Descriptor Matching</h3>
          {matched.length > 0 && (
            <div className="vehicle-desc-line">
              <span className="vehicle-desc-label">Match:</span>
              <span className="vehicle-desc-value vehicle-desc-value--match">
                {matched.join(', ')}
              </span>
            </div>
          )}
          
          {mismatched.length > 0 && (
            <div className="vehicle-desc-line">
              <span className="vehicle-desc-label">Mis:</span>
              <span className="vehicle-desc-value vehicle-desc-value--mismatch">
                {mismatched.join(', ')}
              </span>
            </div>
          )}
                  
          {/* Display all features */}
          <div className="vehicle-desc-line">
            <span className="vehicle-desc-label">Features:</span>
            <span className="vehicle-desc-value">
              {/* Highlight features */}
              {descriptors.map((desc, index) => {
                const category = desc.split(':')[0].trim();
                const isMatched = matched.some(m => m.startsWith(category + ':'));
                const isMismatched = mismatched.some(m => m.startsWith(category + ':'));
                
                return (
                  <span key={index} className={`vehicle-feature-text ${isMatched ? 'vehicle-feature-text--match' : (isMismatched ? 'vehicle-feature-text--mismatch' : '')}`}>
                    {desc}{index < descriptors.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
            </span>
          </div>
          
          <div className="vehicle-desc-line">
            <span className="vehicle-desc-value vehicle-desc-value--highlight">{id}</span>
          </div>
        </div>
        
        {/* Video info area */}
        {videoInfo && (
          <div className="vehicle-video-info">
            <h3 className="vehicle-result-subtitle">Detected Location</h3>
            <p className="vehicle-video-text">{videoInfo}</p>
            
            {/* Embedded video player */}
            {showEmbeddedVideo && (
              <EnhancedVideoPlayer
                videoPath={videoPath}
                startTime={videoStartTime}
                onClose={() => setShowEmbeddedVideo(false)}
                onGoDetail={onGoDetail}
              />
            )}
            
            {/* Button container - with Save and Share buttons */}
            <div className="vehicle-button-container">
              {/* Play Video button */}
              <button 
                className="vehicle-video-button"
                onClick={handlePlayClick}
              >
                <span style={{ marginRight: '5px' }}>{showEmbeddedVideo ? '🛑' : '🎬'}</span>
                {showEmbeddedVideo ? 'Close Video' : 'Play Video'}
              </button>
              
              {/* Save button - Opens save modal */}
              <button 
                className="vehicle-save-button" 
                onClick={() => setShowSaveModal(true)}
              >
                <span style={{ marginRight: '5px' }}>💾</span>
                Save
              </button>
              
              {/* Share button - Opens share modal */}
              <button 
                className="vehicle-share-button" 
                onClick={() => setShowShareModal(true)}
              >
                <span style={{ marginRight: '5px' }}>📤</span>
                Share
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Save Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add to Workspace</h3>
              <button className="modal-close" onClick={() => setShowSaveModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-content">
              {/* Event preview */}
              <div className="modal-event-preview">
                <div className="preview-image">
                  <img 
                    src={imageUrl || `/api/placeholder/200/200?text=Vehicle`}
                    alt={`Vehicle ${id}`}
                    onError={(e) => {
                      e.target.src = `/api/placeholder/200/200?text=Vehicle`;
                    }}
                  />
                </div>
                <div className="preview-info">
                  <h4>Vehicle {id}</h4>
                  <div className="preview-details">
                    {descriptors.length > 0 && (
                      <span className="preview-type">{descriptors[0]}</span>
                    )}
                    <span className="preview-time">{videoInfo}</span>
                    <span className="preview-unit">Unit {robotNumber}</span>
                  </div>
                </div>
              </div>
              
              {/* Note form */}
              <div className="modal-form">
                <div className="form-group">
                  <label>Add Notes</label>
                  <textarea
                    value={saveNote}
                    onChange={e => setSaveNote(e.target.value)}
                    placeholder="Add details about this evidence..."
                    rows={4}
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowSaveModal(false)}>
                Cancel
              </button>
              <button 
                className="modal-btn save" 
                onClick={handleSaveToWorkspace}
              >
                <span style={{ marginRight: '5px' }}>💾</span>
                Save to Workspace
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share with Team</h3>
              <button className="modal-close" onClick={() => setShowShareModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-content">
              {/* Event preview */}
              <div className="modal-event-preview">
                <div className="preview-image">
                  <img 
                    src={imageUrl || `/api/placeholder/200/200?text=Vehicle`}
                    alt={`Vehicle ${id}`}
                    onError={(e) => {
                      e.target.src = `/api/placeholder/200/200?text=Vehicle`;
                    }}
                  />
                </div>
                <div className="preview-info">
                  <h4>Vehicle {id}</h4>
                  <div className="preview-details">
                    {descriptors.length > 0 && (
                      <span className="preview-type">{descriptors[0]}</span>
                    )}
                    <span className="preview-time">{videoInfo}</span>
                    <span className="preview-unit">Unit {robotNumber}</span>
                  </div>
                </div>
              </div>
              
              {/* Message form */}
              <div className="modal-form">
                <div className="form-group">
                  <label>Message for Team</label>
                  <textarea
                    value={shareMessage}
                    onChange={e => setShareMessage(e.target.value)}
                    placeholder="Add context or instructions for your team..."
                    rows={4}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Action Required</label>
                    <select 
                      value={actionRequired}
                      onChange={(e) => setActionRequired(e.target.value)}
                    >
                      <option value="dispatch">Immediate Dispatch</option>
                      <option value="investigate">Investigate ASAP</option>
                      <option value="monitor">Monitor & Update</option>
                      <option value="reference">For Reference Only</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowShareModal(false)}>
                Cancel
              </button>
              <button 
                className="modal-btn share" 
                onClick={handleShareToShared}
                disabled={!shareMessage.trim()}
              >
                <span style={{ marginRight: '5px' }}>📤</span>
                Share with Team
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleTab;

